// frontend/src/pages/Admin/AdminReservationManagementPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './AdminReservationManagementPage.module.css';
import {
    getAllReservationsForAdmin,
    updateReservationStatus,
    deleteReservationForAdmin,
    updateReservationDetails,
} from '../../services/reservationService';
import Button from '../../components/Button/Button';
import Calendar from '../../components/Calendar/Calendar';
import FormInput from '../../components/FormInput/FormInput';
import { getClosures, getFacilityInfo } from '../../services/facilityService';
import { getCalendarBlocks } from '../../services/calendarBlockService';
import FacilityClosureManager from '../../components/Admin/FacilityClosureManager';
import ReservationForm from '../../components/ReservationForm/ReservationForm';
import { useAuth } from '../../contexts/AuthContext';

const generateDateRange = (startDateStr, endDateStr) => {
    const dates = [];
    if (!startDateStr || !endDateStr) return dates;
    const start = new Date(startDateStr + 'T00:00:00Z');
    const end = new Date(endDateStr + 'T00:00:00Z');
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return dates;
    let current = new Date(start);
    while (current <= end) {
        const year = current.getUTCFullYear();
        const month = String(current.getUTCMonth() + 1).padStart(2, '0');
        const day = String(current.getUTCDate()).padStart(2, '0');
        dates.push(`${year}-${month}-${day}`);
        current.setUTCDate(current.getUTCDate() + 1);
    }
    return dates;
};

const getEmailUsername = (email) => {
    if (!email || typeof email !== 'string') return 'N/A';
    const atIndex = email.indexOf('@');
    if (atIndex === -1) return email;
    return email.substring(0, atIndex);
};

const RESERVATION_STATUS_TABS = [
    { label: 'All Active', status: 'all' },
    { label: 'Pending', status: 'pending' },
    { label: 'Approved', status: 'approved' },
    { label: 'Inactive', status: 'inactive' },
];

// Returns a human-readable label
const getStatusLabel = (res) => {
    if (res.status === 'cancelled') {
        if (res.cancellationReason === 'expired_unpaid') return 'Expired';
        return 'Cancelled';
    }
    if (res.status === 'rejected') return 'Declined';
    return res.status.replace('_', ' ');
};

const getStatusClass = (res) => {
    if (res.status === 'rejected') return 'declined';
    return res.status;
};

const AdminReservationManagementPage = () => {
    const { currentUser } = useAuth();
    const [allFetchedReservations, setAllFetchedReservations] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [listError, setListError] = useState(null);
    const [actionLoading, setActionLoading] = useState({ id: null, type: null });
    const [activeFilterStatus, setActiveFilterStatus] = useState('all');
    const [calendarUserFilterId, setCalendarUserFilterId] = useState(null);
    const [calendarDisplayDate, setCalendarDisplayDate] = useState(new Date());
    const [selectedDates, setSelectedDates] = useState([]);
    const [facility, setFacility] = useState(null);
    const [blocks, setBlocks] = useState([]);

    const [expandedReservationId, setExpandedReservationId] = useState(null);
    const [viewingReservation, setViewingReservation] = useState(null);
    const [editingReservationId, setEditingReservationId] = useState(null);
    const [editableEventName, setEditableEventName] = useState('');
    const [editableEventDescription, setEditableEventDescription] = useState('');
    const [editableAdditionalInfo, setEditableAdditionalInfo] = useState('');


    // Unified Fields State
    const [editableGuestName, setEditableGuestName] = useState('');
    const [editableGuestEmail, setEditableGuestEmail] = useState('');
    const [editableGuestPhone, setEditableGuestPhone] = useState('');
    const [editableGroupDescription, setEditableGroupDescription] = useState('');
    const [editablePurposeOfVisit, setEditablePurposeOfVisit] = useState('');
    const [editableAdultCount, setEditableAdultCount] = useState(0);
    const [editableChildCount, setEditableChildCount] = useState(0);
    const [editableResponsiblePerson, setEditableResponsiblePerson] = useState('');

    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [editError, setEditError] = useState(null);

    // Closures State
    const [closures, setClosures] = useState([]);

    const handleToggleExpand = (reservationId) => {
        const newExpandedId = expandedReservationId === reservationId ? null : reservationId;
        setExpandedReservationId(newExpandedId);
        if (newExpandedId !== editingReservationId) {
            setEditingReservationId(null);
            setEditError(null);
        }
    };

    const handleMonthChange = (newDate) => setCalendarDisplayDate(newDate);

    const handleCalendarReservationClick = (details) => {
        setSelectedDates([]); // Clear selection when clicking an existing reservation

        if (viewingReservation?.id === details.id && expandedReservationId === details.id) {
            setViewingReservation(null);
            setExpandedReservationId(null);
            setEditingReservationId(null);
        } else {
            setViewingReservation(details);
            setExpandedReservationId(details.id);
            setEditingReservationId(null);

            // Optionally clear user filter or handle it? My Account just expands.
            // Admin might want the user filter too, but priority is expansion.
            // We set filter status to all to ensure it's visible.
            setActiveFilterStatus('all');

            // Scroll to the expanded row
            setTimeout(() => {
                const element = document.getElementById(`expanded-row-${details.id}`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 10);
        }
    };

    const handleDateSelect = (payload) => {
        // Admin can click anything to start a reservation
        if (typeof payload === 'object' && payload.startDate && payload.endDate) {
            const { startDate, endDate } = payload;
            if (selectedDates[0] === startDate && selectedDates[selectedDates.length - 1] === endDate) {
                setSelectedDates([]);
            } else {
                setSelectedDates(generateDateRange(startDate, endDate));
            }
        } else {
            const clickedDateStr = payload;
            setSelectedDates(prevSelectedDates => {
                const sortedSelection = [...prevSelectedDates].sort();
                const selectionLength = sortedSelection.length;
                if (selectionLength === 0) return [clickedDateStr];
                if (selectionLength === 1) {
                    const [start, end] = [sortedSelection[0], clickedDateStr].sort();
                    if (start === end) return [];
                    return generateDateRange(start, end);
                }
                return [clickedDateStr];
            });
        }
        setCalendarUserFilterId(null);
        setViewingReservation(null);
        setExpandedReservationId(null);
        setEditingReservationId(null);
    };

    const fetchReservationsList = useCallback(async () => {
        setLoadingList(true);
        setListError(null);
        try {
            const [data, closureData, facilityData, blocksData] = await Promise.all([
                getAllReservationsForAdmin(),
                getClosures({ start: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString(), end: new Date(new Date().setFullYear(new Date().getFullYear() + 2)).toISOString() }),
                getFacilityInfo(),
                getCalendarBlocks()
            ]);
            setAllFetchedReservations(data);
            setClosures(closureData);
            setFacility(facilityData);
            setBlocks(blocksData);
        } catch (err) {
            const errorMsg = err.message || 'Could not fetch data.';
            setListError(errorMsg);
            setAllFetchedReservations([]);
        } finally {
            setLoadingList(false);
        }
    }, []);

    useEffect(() => { fetchReservationsList(); }, [fetchReservationsList]);

    const handleTabClick = (status) => {
        setActiveFilterStatus(status);
        setExpandedReservationId(null);
        setEditingReservationId(null);
    };

    const handleStartEdit = (reservation) => {
        setEditingReservationId(reservation.id);
        setEditableEventName(reservation.eventName || '');
        setEditableEventDescription(reservation.eventDescription || '');
        setEditableAdditionalInfo(reservation.additionalInfo || '');

        // Populate Unified Fields
        setEditableGuestName(reservation.guestName || '');
        setEditableGuestEmail(reservation.guestEmail || '');
        setEditableGuestPhone(reservation.guestPhone || '');
        setEditableGroupDescription(reservation.groupDescription || '');
        setEditablePurposeOfVisit(reservation.purposeOfVisit || '');
        setEditableAdultCount(reservation.adultCount || 0);
        setEditableChildCount(reservation.childCount || 0);
        setEditableResponsiblePerson(reservation.responsiblePerson || '');

        setEditError(null);
    };

    const handleCancelEdit = () => {
        setEditingReservationId(null);
        setEditError(null);
    };

    const handleSaveChanges = async (reservationId) => {
        if (!editableGuestName.trim() || !editableResponsiblePerson.trim()) {
            setEditError('Contact Name and Responsible Person are required.');
            return;
        }

        // Auto-maintain Event Name/Description if not explicitly set (though Admin might want full control)
        // We'll enforce that eventName is present.
        let finalEventName = editableEventName.trim();
        if (!finalEventName) {
            finalEventName = editableGroupDescription ? `${editableGroupDescription} Reservation` : `Reservation for ${editableGuestName}`;
        }
        let finalDescription = editableEventDescription.trim();
        if (!finalDescription) {
            finalDescription = editablePurposeOfVisit || 'No description provided';
        }

        setIsSavingEdit(true);
        setEditError(null);
        const detailsToUpdate = {
            eventName: finalEventName,
            eventDescription: finalDescription,
            additionalInfo: editableAdditionalInfo.trim(),
            // Unified Fields
            guestName: editableGuestName.trim(),
            guestEmail: editableGuestEmail.trim(),
            guestPhone: editableGuestPhone.trim(),
            groupDescription: editableGroupDescription.trim(),
            purposeOfVisit: editablePurposeOfVisit.trim(),
            adultCount: editableAdultCount,
            childCount: editableChildCount,
            responsiblePerson: editableResponsiblePerson.trim()
        };

        try {
            await updateReservationDetails(reservationId, detailsToUpdate);
            await fetchReservationsList();
            setEditingReservationId(null);
        } catch (apiError) {
            console.error('Failed to save reservation details (Admin):', apiError);
            setEditError(apiError.response?.data?.message || apiError.message || 'Failed to update reservation details.');
        } finally {
            setIsSavingEdit(false);
        }
    };

    const filteredTableReservations = useMemo(() => {
        let filtered = allFetchedReservations;
        if (calendarUserFilterId) {
            filtered = filtered.filter(res => res.userId === calendarUserFilterId);
        }
        if (activeFilterStatus === 'all') {
            return filtered.filter(res => res.status === 'pending' || res.status === 'approved')
                .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
        }
        if (activeFilterStatus === 'inactive') {
            return filtered.filter(res => res.status === 'cancelled' || res.status === 'rejected')
                .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
        }
        return filtered.filter(res => res.status === activeFilterStatus)
            .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    }, [allFetchedReservations, activeFilterStatus, calendarUserFilterId]);

    const filteredCalendarReservations = useMemo(() => {
        let reservationsSource = allFetchedReservations;

        const year = calendarDisplayDate.getFullYear();
        const month = calendarDisplayDate.getMonth();

        reservationsSource = reservationsSource.filter(res => {
            if (!res.startTime || !res.endTime) return false;
            const reservationStart = new Date(res.startTime);
            const reservationEnd = new Date(res.endTime);

            const firstDayOfCalendarMonth = new Date(Date.UTC(year, month, 1));
            const lastDayOfCalendarMonth = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999));

            return reservationStart <= lastDayOfCalendarMonth && reservationEnd >= firstDayOfCalendarMonth;
        });

        if (calendarUserFilterId) {
            reservationsSource = reservationsSource.filter(
                (res) => res.userId === calendarUserFilterId
            );
        }

        if (activeFilterStatus === 'all') {
            return reservationsSource.filter(res => res.status === 'pending' || res.status === 'approved');
        } else if (activeFilterStatus === 'inactive') {
            return reservationsSource.filter(res => res.status === 'cancelled' || res.status === 'rejected');
        } else {
            return reservationsSource.filter(res => res.status === activeFilterStatus);
        }
    }, [allFetchedReservations, calendarDisplayDate, activeFilterStatus, calendarUserFilterId]);

    const adminReservedDatesMap = useMemo(() => {
        const map = new Map();
        filteredCalendarReservations.forEach(res => {
            const startDate = res.startTime?.split('T')[0];
            const endDate = res.endTime?.split('T')[0];
            if (startDate && endDate) {
                generateDateRange(startDate, endDate).forEach(dateStr => {
                    map.set(dateStr, {
                        ...res,
                        displayUsername: getEmailUsername(res.user?.username || res.user?.email),
                        type: res.status
                    });
                });
            }
        });


        // Merge Closures
        closures.forEach(closure => {
            const startDate = closure.startTime.split('T')[0];
            const endDate = closure.endTime.split('T')[0];
            if (startDate && endDate) {
                generateDateRange(startDate, endDate).forEach(dateStr => {
                    // Closures overwrite reservations visually or coexist? 
                    // Usually closure means "no reservation possible", so it should take precedence visually or show error if conflict exists
                    // But for Admin, show it as closure.
                    map.set(dateStr, {
                        id: closure.id,
                        text: 'Unavailable', // Changed from Closed: reason
                        reason: closure.reason,
                        statusLabel: 'Unavailable', // For potential future use
                        type: 'closure', // Renders hatched pattern
                        startTime: closure.startTime,
                        endTime: closure.endTime,
                        batchId: closure.batchId,
                        // Keep reason for tooltip if needed, Calendar component uses title={...text...} or custom title logic?
                        // Calendar.jsx: title={dayInfo.type === 'closure' ? (dayInfo.reservationDetails?.text || 'Facility Closed') : ''}
                        // If we want tooltip to show reason, we might need to adjust Calendar.jsx or put it in text?
                        // User asked "Instead of closed can we say unavailable".
                        // I will keep text as 'Unavailable'.
                        // I'll add a separate 'reason' field if I want to update Calendar to show it in title.
                        // But for now, let's stick to user request.
                    });
                });
            }
        });

        return map;
    }, [filteredCalendarReservations, closures]);

    const handleActionAndUpdate = async (actionPromise) => {
        setListError(null);
        setEditError(null);
        try {
            await actionPromise;
            await fetchReservationsList();
        } catch (err) {
            setListError(err.message || 'An error occurred performing the action.');
        } finally {
            setActionLoading({ id: null, type: null });
        }
    };

    const handleApprove = (id) => {
        setActionLoading({ id, type: 'approve' });
        handleActionAndUpdate(updateReservationStatus(id, { status: 'approved' }));
    };
    const handleReject = (id) => {
        const reason = prompt("Optional: Enter reason for rejection:");
        if (reason !== null) {
            setActionLoading({ id, type: 'reject' });
            handleActionAndUpdate(updateReservationStatus(id, { status: 'rejected', adminNotes: reason || undefined }));
        } else {
            setActionLoading({ id: null, type: null });
        }
    };
    const handleAdminCancel = (id) => {
        if (window.confirm("Are you sure you want to CANCEL this reservation?")) {
            setActionLoading({ id, type: 'cancel' });
            handleActionAndUpdate(updateReservationStatus(id, { status: 'cancelled', cancellationReason: 'admin_cancelled' }));
        } else {
            setActionLoading({ id: null, type: null });
        }
    };
    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to DELETE this reservation? This cannot be undone.")) {
            setActionLoading({ id, type: 'delete' });
            handleActionAndUpdate(deleteReservationForAdmin(id));
        } else {
            setActionLoading({ id: null, type: null });
        }
    };

    const isButtonLoading = (id, type) => actionLoading.id === id && actionLoading.type === type;
    const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';

    const filteredUserEmail = useMemo(() => {
        if (!calendarUserFilterId) return null;
        const userRes = allFetchedReservations.find(r => r.userId === calendarUserFilterId);
        return userRes?.user?.username || userRes?.user?.email || `User ID: ${calendarUserFilterId}`;
    }, [calendarUserFilterId, allFetchedReservations]);

    return (
        <div className={styles.adminReservationsContainer}>
            <h2 className="page-main-title">Reservation Management</h2>

            <p>Filter reservations by status or click on the calendar to filter by user.</p>
            <div className={styles.calendarSection}>
                {listError && !loadingList && <p className={styles.errorMessage}>{listError}</p>}
                <Calendar
                    currentDisplayDate={calendarDisplayDate}
                    onMonthChange={handleMonthChange}
                    isLoading={loadingList}
                    reservedDatesMap={adminReservedDatesMap}
                    onShowDetails={handleCalendarReservationClick}
                    onDateSelect={handleDateSelect}
                    selectedDates={selectedDates}
                    viewingReservation={viewingReservation}
                    selectionMode={facility?.selectionMode || 'free'}
                    blocks={blocks}
                />
            </div>

            {selectedDates.length > 0 && (
                <div className={styles.newReservationFormSection} style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                    <ReservationForm
                        key={selectedDates.join('-')}
                        selectedDates={selectedDates}
                        currentUser={currentUser}
                        onReservationSuccess={() => {
                            setSelectedDates([]);
                            fetchReservationsList();
                        }}
                        onClearView={() => setSelectedDates([])}
                        formTitle="Add New Reservation (Admin Override)"
                        formContext="admin"
                    />
                </div>
            )}
            <div className={styles.tabsContainer}>
                {RESERVATION_STATUS_TABS.map(tab => (
                    <button
                        key={tab.status}
                        className={`${styles.tabButton} ${activeFilterStatus === tab.status ? styles.activeTab : ''}`}
                        onClick={() => handleTabClick(tab.status)}
                    >
                        {tab.label} ({
                            tab.status === 'all'
                                ? allFetchedReservations.filter(r => (r.status === 'pending' || r.status === 'approved') && (!calendarUserFilterId || r.userId === calendarUserFilterId)).length
                                : tab.status === 'inactive'
                                    ? allFetchedReservations.filter(r => (r.status === 'cancelled' || r.status === 'rejected') && (!calendarUserFilterId || r.userId === calendarUserFilterId)).length
                                    : allFetchedReservations.filter(r => r.status === tab.status && (!calendarUserFilterId || r.userId === calendarUserFilterId)).length
                        })
                    </button>
                ))}
            </div>
            <div className={styles.listSection}>
                <h3>
                    {RESERVATION_STATUS_TABS.find(t => t.status === activeFilterStatus)?.label || 'Reservations'}
                    {calendarUserFilterId && ` for User: ${filteredUserEmail}`}
                </h3>
                {calendarUserFilterId && (
                    <Button onClick={() => { setCalendarUserFilterId(null); setExpandedReservationId(null); setEditingReservationId(null); }} variant="secondary" size="sm" style={{ marginBottom: '10px' }}>
                        Clear User Filter
                    </Button>
                )}
                {listError && !loadingList && !editError && <p className={styles.errorMessage}>{listError}</p>}
                {loadingList && <p>Loading reservations list...</p>}
                {!loadingList && (
                    <div className={styles.tableContainer}>
                        {filteredTableReservations.length === 0 && !listError ? (
                            <p>No reservations found for this filter.</p>
                        ) : (
                            <table className={styles.reservationTable}>
                                <thead>
                                    {/* --- MODIFIED TABLE HEADERS --- */}
                                    <tr>
                                        <th>Event Name</th>
                                        <th>User</th>
                                        <th>Dates</th>
                                        <th>Status</th>
                                        <th>Agreement</th>
                                        <th>Payment</th>
                                        <th>Actions</th>
                                    </tr>
                                    {/* --- END MODIFIED TABLE HEADERS --- */}
                                </thead>
                                <tbody>
                                    {filteredTableReservations.map((res) => (
                                        <React.Fragment key={`reservation-group-${res.id}`}>
                                            <tr
                                                id={`reservation-row-${res.id}`}
                                                className={`${styles.reservationRowClickable} ${expandedReservationId === res.id ? styles.activeExpandedRow : ''}`}
                                                onClick={() => handleToggleExpand(res.id)}
                                            >
                                                {/* --- MODIFIED TABLE CELLS (ID removed, Dates combined) --- */}
                                                <td>{res.eventName || 'N/A'}</td>
                                                <td>{res.user?.username || res.user?.email || 'N/A'}</td>
                                                <td>{`${formatDate(res.startTime)} - ${formatDate(res.endTime)}`}</td>
                                                <td><span className={`${styles.status} ${styles[getStatusClass(res)] || styles.pending}`}>{getStatusLabel(res)}</span></td>
                                                <td>
                                                    <span className={`${styles.statusBadge} ${(['signed', 'signed_manual'].includes(res.agreementStatus)) ? styles.statusSigned : styles.statusPending}`}>
                                                        {(['signed', 'signed_manual'].includes(res.agreementStatus)) ? '✓ Signed' : 'Pending'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`${styles.statusBadge} ${(['paid', 'paid_manual'].includes(res.paymentStatus)) ? styles.statusPaid : styles.statusUnpaid}`}>
                                                        {(['paid', 'paid_manual'].includes(res.paymentStatus)) ? '✓ Paid' : 'Unpaid'}
                                                    </span>
                                                </td>
                                                {/* --- END MODIFIED TABLE CELLS --- */}
                                                <td
                                                    className={styles.actionButtonsCellContainer}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <div className={styles.actionButtonsGroup}>
                                                        {(activeFilterStatus === 'inactive') && (res.status === 'cancelled' || res.status === 'rejected') ? (
                                                            <Button onClick={() => handleDelete(res.id)} disabled={isButtonLoading(res.id, 'delete')} variant="secondary" size="sm" className={styles.deleteButtonHoverEffect}>
                                                                {isButtonLoading(res.id, 'delete') ? '...' : 'Delete'}
                                                            </Button>
                                                        ) : (
                                                            <>
                                                                {res.status !== 'cancelled' && res.status !== 'rejected' && (
                                                                    <>
                                                                        <Button onClick={() => handleApprove(res.id)} disabled={isButtonLoading(res.id, 'approve') || res.status === 'approved'} variant="secondary" size="sm" className={styles.approveBtn}>
                                                                            {isButtonLoading(res.id, 'approve') ? '...' : 'Approve'}
                                                                        </Button>
                                                                        <Button onClick={() => handleReject(res.id)} disabled={isButtonLoading(res.id, 'reject') || res.status === 'rejected'} variant="secondary" size="sm">
                                                                            {isButtonLoading(res.id, 'reject') ? '...' : 'Decline'}
                                                                        </Button>
                                                                    </>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                            {expandedReservationId === res.id && (
                                                <tr id={`expanded-row-${res.id}`} className={styles.expandedDetailRow}>
                                                    {/* --- MODIFIED COLSPAN --- */}
                                                    <td colSpan={7}>
                                                        {/* --- END MODIFIED COLSPAN --- */}
                                                        {editingReservationId === res.id ? (
                                                            <div className={styles.editFormInTable} style={{ textAlign: 'left' }}>
                                                                <h4>Editing: {res.eventName}</h4>

                                                                <div style={{ marginBottom: '1rem' }}>
                                                                    <h5 style={{ margin: '0.5rem 0' }}>1. Name of Person Initiating Reservation</h5>
                                                                    <FormInput label="Full Name" type="text" value={editableGuestName} onChange={(e) => setEditableGuestName(e.target.value)} required />

                                                                    <div style={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
                                                                        <h6 style={{ margin: '0.5rem 0', color: '#666' }}>a) Contact Information</h6>
                                                                        <div style={{ display: 'flex', gap: '10px' }}>
                                                                            <FormInput label="Email" type="email" value={editableGuestEmail} onChange={(e) => setEditableGuestEmail(e.target.value)} containerStyle={{ flex: 1 }} required />
                                                                            <FormInput label="Phone" type="tel" value={editableGuestPhone} onChange={(e) => setEditableGuestPhone(e.target.value)} containerStyle={{ flex: 1 }} required />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Section 2: Account/Church (Admin can verify user but editing might be changing the reservation owner? Not usually. Leaving out for now) */}

                                                                <div style={{ marginBottom: '1rem' }}>
                                                                    <h5 style={{ margin: '0.5rem 0' }}>3. Description of Group</h5>
                                                                    <FormInput label="Group Description" type="text" value={editableGroupDescription} onChange={(e) => setEditableGroupDescription(e.target.value)} placeholder="i.e., College or District retreat; Service Group, etc." />

                                                                    <div style={{ marginTop: '0.5rem', fontSize: '0.9em' }}>
                                                                        <label style={{ display: 'block', marginBottom: '5px', color: '#555' }}>Event Title (Calendar Display)</label>
                                                                        <input type="text" value={editableEventName} onChange={(e) => setEditableEventName(e.target.value)} style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
                                                                    </div>
                                                                </div>

                                                                <div style={{ marginBottom: '1rem' }}>
                                                                    <h5 style={{ margin: '0.5rem 0' }}>4. Purpose of Highview House Visit</h5>
                                                                    <FormInput label="Purpose of Visit" type="textarea" value={editablePurposeOfVisit} onChange={(e) => setEditablePurposeOfVisit(e.target.value)} rows={3} />
                                                                </div>

                                                                <div style={{ marginBottom: '1rem' }}>
                                                                    <h5 style={{ margin: '0.5rem 0' }}>5. Approximate Number of Attendees</h5>
                                                                    <div style={{ display: 'flex', gap: '10px' }}>
                                                                        <FormInput label="Adults" type="number" value={editableAdultCount} onChange={(e) => setEditableAdultCount(e.target.value)} min="0" containerStyle={{ flex: 1 }} />
                                                                        <FormInput label="Children" type="number" value={editableChildCount} onChange={(e) => setEditableChildCount(e.target.value)} min="0" containerStyle={{ flex: 1 }} />
                                                                    </div>
                                                                </div>

                                                                <div style={{ marginBottom: '1rem' }}>
                                                                    <h5 style={{ margin: '0.5rem 0' }}>6. Person Responsible for Payment Arrangements</h5>
                                                                    <FormInput label="Responsible Person" type="text" value={editableResponsiblePerson} onChange={(e) => setEditableResponsiblePerson(e.target.value)} required />
                                                                </div>

                                                                <FormInput
                                                                    label="Additional Internal Info"
                                                                    type="textarea"
                                                                    value={editableAdditionalInfo}
                                                                    onChange={(e) => setEditableAdditionalInfo(e.target.value)}
                                                                    placeholder="Internal notes, Special Requests, etc."
                                                                />

                                                                {editError && <p className={styles.errorMessageModal || styles.errorMessage}>{editError}</p>}
                                                                <div className={styles.editActions}>
                                                                    <Button
                                                                        onClick={(e) => { e.stopPropagation(); handleSaveChanges(res.id); }}
                                                                        variant="success"
                                                                        size="sm"
                                                                        disabled={isSavingEdit || !editableEventName.trim() || !editableEventDescription.trim()}
                                                                    >
                                                                        {isSavingEdit ? 'Saving...' : 'Save Changes'}
                                                                    </Button>
                                                                    <Button
                                                                        onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}
                                                                        variant="secondary"
                                                                        size="sm"
                                                                        disabled={isSavingEdit}
                                                                    >
                                                                        Cancel Edit
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className={styles.readOnlyDetailsContainer}>
                                                                <p><strong>Event Name:</strong> {res.eventName || 'N/A'}</p>
                                                                {res.eventDescription && <p><strong>Event Description:</strong> {res.eventDescription}</p>}

                                                                {/* Unified Details View */}
                                                                <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem', padding: '0.5rem', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                                                                    <p style={{ margin: '0.25rem 0' }}><strong>Guest:</strong> {res.guestName} &lt;{res.guestEmail}&gt; {res.guestPhone ? `| Phone: ${res.guestPhone}` : ''}</p>
                                                                    {res.groupDescription && <p style={{ margin: '0.25rem 0' }}><strong>Group:</strong> {res.groupDescription}</p>}
                                                                    {res.purposeOfVisit && <p style={{ margin: '0.25rem 0' }}><strong>Purpose:</strong> {res.purposeOfVisit}</p>}
                                                                    {(res.adultCount > 0 || res.childCount > 0) && <p style={{ margin: '0.25rem 0' }}><strong>Attendees:</strong> {res.adultCount} Adults, {res.childCount} Children</p>}
                                                                    {res.responsiblePerson && <p style={{ margin: '0.25rem 0' }}><strong>Responsible Person:</strong> {res.responsiblePerson}</p>}
                                                                </div>

                                                                {res.additionalInfo && <p><strong>Additional Info:</strong> {res.additionalInfo}</p>}
                                                                {res.adminNotes && <p><strong>Admin Notes:</strong> {res.adminNotes}</p>}
                                                                {res.cancellationReason && <p><strong>Cancellation Reason:</strong> {res.cancellationReason}</p>}
                                                                <p><strong>Requested by (Account):</strong> {res.user?.username || res.user?.email || 'N/A'}</p>

                                                                <p><strong>Dates:</strong> {formatDate(res.startTime)} - {formatDate(res.endTime)}</p>
                                                                <p><strong>Current Status:</strong> <span className={`${styles.status} ${styles[getStatusClass(res)] || styles.pending}`}>{getStatusLabel(res)}</span></p>

                                                                <div className={styles.expandedActions}>
                                                                    {!['cancelled', 'rejected'].includes(res.status) && (
                                                                        <Button
                                                                            onClick={(e) => { e.stopPropagation(); handleStartEdit(res); }}
                                                                            variant="outlinePrimary"
                                                                            size="sm"
                                                                            className={styles.expandedActionButton}
                                                                        >
                                                                            Edit Reservation
                                                                        </Button>
                                                                    )}
                                                                    {res.status !== 'cancelled' && res.status !== 'rejected' && (
                                                                        <Button
                                                                            onClick={(e) => { e.stopPropagation(); handleAdminCancel(res.id); }}
                                                                            disabled={isButtonLoading(res.id, 'cancel')}
                                                                            variant="outlineDanger"
                                                                            size="sm"
                                                                            className={styles.expandedActionButton}
                                                                        >
                                                                            {isButtonLoading(res.id, 'cancel') ? '...' : 'Cancel Reservation'}
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminReservationManagementPage;